// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/AltoRendimientoPage.jsx
//  ALTO RENDIMIENTO — ruta /alto-rendimiento
//
//  Los siete entrenamientos para B2 en adelante, en un solo sitio.
//
//  ── POR QUÉ UNA SALA PROPIA Y NO SEIS FORMATOS MÁS EN EL DOJO ───────────────
//  El Dojo promete otra cosa: retos de menos de minuto y medio que entrenan
//  reflejos. Aquí un debate dura dos minutos y medio y un correo puede llevar
//  diez. Meterlos juntos rompería la promesa del Dojo y escondería estos siete
//  en una lista de once tarjetas.
//
//  Y sobre todo: la auditoría de niveles dijo que el alumno intermedio y
//  avanzado no encuentra su sitio en NSL. Darle un sitio con nombre es la mitad
//  de la solución.
//
//  AISLAMIENTO: todo vive en `src/advanced/` y habla sólo con
//  `/api/v3/advanced`. Cuatro de los siete formatos ni siquiera llegan ahí.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import usePageTracker from '../hooks/usePageTracker.js';

import ChoiceDrill from '../advanced/ChoiceDrill';
import DebateRoom from '../advanced/DebateRoom';
import EmailDesk from '../advanced/EmailDesk';
import SituacionRoom from '../advanced/SituacionRoom';
import { DRILLS } from '../advanced/advancedBank';
import { getCatalog, getIdioma } from '../advanced/advancedApi';
import { textosSala } from '../advanced/salaTextos';
// `availableAccents` y `ACCENTS` se usaban sólo para el aviso de «tu sistema
// sólo tiene N voces inglesas», que Rob quitó el 27-09-2026. La sala sigue
// hablando con las voces que haya: quien las elige es `advancedSpeech`, no
// esta pantalla.

import '../advanced/advanced.css';

/** Los tres formatos que necesitan servidor, presentados como los otros cuatro.
 *
 *  El color ya no es un hexadecimal fijo de modo oscuro: es un token que se
 *  resuelve por tema. Y ninguno de los siete es rojo ni verde — en NSL esos dos
 *  significan error y acierto, y "Precision lexica" en verde se leia como un
 *  acierto ya dado antes de tocarla.
 *
 *  Aqui NO hay palabras. El nombre, la promesa y lo que entrena cada formato
 *  viven en `salaTextos.js` porque cambian de idioma; lo que queda aqui es lo
 *  que no cambia nunca: el identificador, el icono, el nivel y el color. Es la
 *  misma division que en el resto de NSL — el codigo manda identificadores y la
 *  pantalla pone las palabras. */
const AI_CARDS = [
  { id: 'debate', icon: '⚔️', level: 'C1 – C2', accent: 'var(--ar-f-debate)' },
  { id: 'email', icon: '✉️', level: 'B2 – C1', accent: 'var(--ar-f-email)' },
  {
    // El septimo, y el unico que NO mide como hablas sino si CONSEGUISTE algo.
    // Tambien el unico que se abre en su propio panel de perillas en vez de con
    // una escena sorteada: la configuracion es el formato, no un ajuste.
    id: 'situacion', icon: '🤝', level: 'B2 – C2',
    accent: 'var(--ar-f-situacion)', config: true,
  },
];

function pick(list) {
  return list[Math.floor(Math.random() * list.length)];
}

export default function AltoRendimientoPage() {
  usePageTracker('alto-rendimiento', 'Alto Rendimiento', null, 'studying');

  const navigate = useNavigate();
  const [active, setActive] = useState(null);      // id del formato
  const [catalog, setCatalog] = useState(null);
  const [topic, setTopic] = useState(null);
  const [side, setSide] = useState('for');
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);

  // El idioma de la sala. Lo resuelve el servidor —es el unico que sabe el
  // nivel del alumno— y si la llamada falla se queda en español y la sala
  // funciona igual: el idioma nunca puede ser motivo de que algo no se abra.
  const [idioma, setIdioma] = useState('es');
  const T = textosSala(idioma);

  useEffect(() => {
    let vivo = true;
    let elegido = 'auto';
    try { elegido = localStorage.getItem('nsl_coach_idioma') || 'auto'; } catch { /* modo privado */ }
    getIdioma(elegido)
      .then((r) => { if (vivo && r?.idioma) setIdioma(r.idioma); })
      .catch(() => { /* se queda en español */ });
    return () => { vivo = false; };
  }, []);

  const openDebate = async () => {
    setError(null);
    try {
      const c = catalog || await getCatalog();
      setCatalog(c);
      setTopic(pick(c.debates));
      setSide(Math.random() < 0.5 ? 'for' : 'against');   // la postura se sortea
      setActive('debate');
    } catch (e) { setError(e.message); }
  };

  const openEmail = async () => {
    setError(null);
    try {
      const c = catalog || await getCatalog();
      setCatalog(c);
      setBrief(pick(c.emails));
      setActive('email');
    } catch (e) { setError(e.message); }
  };

  // Cada formato con IA sabe abrirse solo. Un mapa en vez de un ternario
  // anidado: al añadir el octavo no hay que tocar el JSX.
  const ABRE = {
    debate: openDebate,
    email: openEmail,
    situacion: () => { setError(null); setActive('situacion'); },
  };

  const drill = DRILLS[active];
  const back = () => { setActive(null); setTopic(null); setBrief(null); };

  return (
    <>
      <Header />
      <main className="ar-root">
        <div className="ar-shell">
          {drill ? (
            <ChoiceDrill drill={drill} onExit={back} idioma={idioma} />
          ) : active === 'debate' && topic ? (
            <DebateRoom topic={topic} side={side} onExit={back} onRestart={openDebate}
                        idioma={idioma} />
          ) : active === 'email' && brief ? (
            <EmailDesk brief={brief} onExit={back} onRestart={openEmail} idioma={idioma} />
          ) : active === 'situacion' ? (
            <SituacionRoom idiomaInicial={idioma} onExit={back} />
          ) : (
            <div className="ar-screen">
              <header className="ar-hero">
                <div className="ar-tag">{T.tag}</div>
                <h1 className="ar-h1">
                  <span className="ar-ac">{T.h1a}</span>
                  <span className="ar-glow">{T.h1b}</span>
                </h1>
                <p className="ar-lead">
                  {T.lead1} <i>big problem</i> {T.lead2} <i>pressing issue</i>
                  {T.lead3}
                </p>
              </header>

              {error && <div className="ar-fail" style={{ marginBottom: '1rem' }}>{error}</div>}

              <div className="ar-grid">
                {Object.values(DRILLS).map((d) => (
                  <button
                    key={d.id} type="button" className="ar-card"
                    style={{ '--ar-mc': d.accent }} onClick={() => setActive(d.id)}
                  >
                    <span className="ar-card-glow" aria-hidden="true" />
                    <span className="ar-card-icon" aria-hidden="true">{d.icon}</span>
                    <span className="ar-card-lvl">{d.level}</span>
                    <span className="ar-card-name">{T.formatos[d.id].name}</span>
                    <span className="ar-card-claim">{T.formatos[d.id].claim}</span>
                    <span className="ar-card-foot">
                      {T.formatos[d.id].trains} · {T.reactivos(d.bank.length)}
                    </span>
                    <span className="ar-card-arrow" aria-hidden="true">→</span>
                    <span className="ar-card-bar" aria-hidden="true" />
                  </button>
                ))}

                {AI_CARDS.map((d) => (
                  <button
                    key={d.id} type="button" className="ar-card"
                    style={{ '--ar-mc': d.accent }}
                    onClick={ABRE[d.id]}
                  >
                    <span className="ar-card-glow" aria-hidden="true" />
                    <span className="ar-card-icon" aria-hidden="true">{d.icon}</span>
                    <span className="ar-card-lvl">{d.level}</span>
                    <span className="ar-card-name">{T.formatos[d.id].name}</span>
                    <span className="ar-card-claim">{T.formatos[d.id].claim}</span>
                    <span className="ar-card-foot">
                      {T.formatos[d.id].trains} · {d.config ? T.configurable : T.conIA}
                    </span>
                    <span className="ar-card-arrow" aria-hidden="true">→</span>
                    <span className="ar-card-bar" aria-hidden="true" />
                  </button>
                ))}
              </div>

              {/* ── AQUÍ HABÍA DOS AVISOS, Y LOS QUITÓ ROB (27-09-2026) ──────
                  Uno decía cuáles de los siete formatos funcionan sin conexión
                  y cuáles necesitan servidor. El otro, cuántas voces inglesas
                  tiene el aparato del alumno y que por eso «Oído nativo no
                  podrá variar el acento tanto como debería».

                  Los dos estaban escritos desde dentro: hablan de servidores,
                  de IA y de las voces del sistema operativo. Al alumno que
                  llega a entrenar no le resuelven nada y le siembran la duda
                  de si esto va a funcionarle — justo antes de elegir.

                  Lo que sí importaba de ellos sigue vivo donde toca: el pie de
                  cada tarjeta ya dice si ese formato usa IA. */}

              <div className="ar-actions">
                <button type="button" className="ar-btn ar-btn-ghost" onClick={() => navigate('/entrenar')}>
                  {T.volver}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
